import { SearchParameters } from "@/types/dto/search-filters/search-parameters";
import {
  convertDurationToRange,
  convertRangeToDuration,
  getLocationFieldNames,
  mapInternalToApiField,
  getFieldsByType
} from "@/config/unified-search-config";

/**
 * Converts a SearchParameters value to an array format for multi-select components
 */
const ensureArray = (value: string | string[] | number | undefined): string[] => {
  if (value === undefined || value === null) {
    return [];
  }

  if (Array.isArray(value)) {
    return value.map(v => v.toString());
  }
  return [value.toString()];
};

/**
 * Converts SearchParameters to the format expected by filter components
 * Each field becomes a string array for consistent multi-select handling
 */
export const searchParametersToFilterValues = (
  searchParams: Partial<SearchParameters>
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};
  // Process all standard fields with explicit mapping
  result.Continent = ensureArray(searchParams.Continent);
  result.Country = ensureArray(searchParams.Country);
  result.Region = ensureArray(searchParams.Region);
  result.City = ensureArray(searchParams.City);
  result.Badges = ensureArray(searchParams.Badges);
  result.Offers = ensureArray(searchParams.Offers);
  result.TourStyle = ensureArray(searchParams.TourStyles); // Note: TourStyles -> TourStyle
  result.Duration = ensureArray(searchParams.Duration);
  result.ActivityLevel = ensureArray(searchParams.ActivityLevel);
  result.CustomerReview = ensureArray(searchParams.CustomerReview);
  result.keyword = ensureArray(searchParams.keyword);

  result.months = ensureArray(searchParams.months);

  // Handle special case: convert TourLengthFrom/TourLengthTo back to TourLengthDescription
  const durationValues = searchParams.TourLengthDescription
    ? ensureArray(searchParams.TourLengthDescription)
    : convertRangeToDuration(searchParams.TourLengthFrom, searchParams.TourLengthTo);

  result.TourLengthDescription = durationValues;
  return result;
};

/**
 * Extracts price range values from SearchParameters
 */
export const searchParametersToPriceRange = (
  searchParams: Partial<SearchParameters>
): Record<string, string> => ({
  PriceFrom: searchParams.PriceFrom?.toString() || "",
  PriceTo: searchParams.PriceTo?.toString() || ""
});

/**
 * Extracts date range values from SearchParameters
 */
export const searchParametersToDateRange = (
  searchParams: Partial<SearchParameters>
): Record<string, string> => ({
  DepartDateFrom: searchParams.DepartDateFrom?.toString() || "",
  DepartDateTo: searchParams.DepartDateTo?.toString() || ""
});

/**
 * Extracts tour length range values from SearchParameters
 */
export const searchParametersToTourLengthRange = (
  searchParams: Partial<SearchParameters>
): Record<string, string> => ({
  TourLengthFrom: searchParams.TourLengthFrom?.toString() || "",
  TourLengthTo: searchParams.TourLengthTo?.toString() || ""
});

export const searchParametersToSortBy = (
  searchParams: Partial<SearchParameters>
): Record<string, string> => ({
  sortBy: searchParams.sortBy?.toString() || ""
});

/**
 * The reverse operation - converts filter values back to SearchParameters
 * Useful for building search requests from component state
 */
export const filterValuesToSearchParameters = (
  filterValues: Record<string, string[]>,
  priceRange?: Record<string, string>,
  dateRange?: Record<string, string>,
  tourLengthRange?: Record<string, string>
): Partial<SearchParameters> => {
  const searchParams: Partial<SearchParameters> = {};

  // Add array-based filters (only if they have values)
  Object.entries(filterValues).forEach(([key, value]) => {
    // Handle special case: TourLengthDescription should be converted to TourLengthFrom/TourLengthTo
    if (key === "TourLengthDescription") {
      if (value && value.length > 0) {
        const durationRange = convertDurationToRange(value);
        if (durationRange.from !== undefined) {
          searchParams.TourLengthFrom = durationRange.from;
        }
        if (durationRange.to !== undefined) {
          searchParams.TourLengthTo = durationRange.to;
        }
      } else {
        // Clear the range if no duration selected
        searchParams.TourLengthFrom = undefined;
        searchParams.TourLengthTo = undefined;
      }
      return; // Don't process as regular array field
    }

    // Get all valid array fields from unified config
    const validArrayFields = getFieldsByType("array");

    // Only process if it's a valid SearchParameters field
    if (validArrayFields.includes(key as any)) {
      if (value && value.length > 0) {
        // Apply outgoing API parameter mappings (e.g., TourStyle → TourStyles)
        const apiFieldName = mapInternalToApiField(key);
        // Add the field with values
        (searchParams as any)[apiFieldName] = value.length === 1 ? value[0] : value;
      } else {
        // Apply outgoing API parameter mappings for deletion as well
        const apiFieldName = mapInternalToApiField(key);
        // Explicitly set to undefined to remove from search parameters
        (searchParams as any)[apiFieldName] = undefined;
      }
    }
  });

  // Add price range filters
  if (priceRange) {
    Object.entries(priceRange).forEach(([key, value]) => {
      if (value && value.trim() !== "") {
        const numValue = Number(value);
        if (!Number.isNaN(numValue)) {
          (searchParams as any)[key] = numValue;
        }
      }
    });
  }

  // Add date range filters
  if (dateRange) {
    Object.entries(dateRange).forEach(([key, value]) => {
      if (value && value.trim() !== "") {
        (searchParams as any)[key] = value;
      }
    });
  }

  // Add tour length range filters
  if (tourLengthRange) {
    Object.entries(tourLengthRange).forEach(([key, value]) => {
      if (value && value.trim() !== "") {
        const numValue = Number(value);
        if (!Number.isNaN(numValue)) {
          (searchParams as any)[key] = numValue;
        }
      }
    });
  }

  return searchParams;
};

/**
 * Helper function to get location filter values in a grouped format
 * Useful for location filter components that might group by type
 */
export const getLocationFilterValues = (
  searchParams: Partial<SearchParameters>
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  // Get all location field names from unified config
  const locationFields = getLocationFieldNames();

  locationFields.forEach(fieldName => {
    result[fieldName] = ensureArray((searchParams as any)[fieldName]);
  });

  return result;
};

/**
 * Converts SearchParameters to the format expected by AppliedFilters component
 * Maps search parameter values to their display names using the filters data
 */
export const convertSearchFiltersToAppliedFilters = (
  searchParams: Partial<SearchParameters>,
  filtersData: import("@/types/dto/search-filters/search-filters").SearchFilters[]
): Record<string, string[]> => {
  const appliedFilters: Record<string, string[]> = {};

  if (!searchParams || !filtersData) {
    return appliedFilters;
  }

  // Helper function to find display name for a value
  const findDisplayName = (fieldName: string, value: string): string => {
    const filter = filtersData.find(f => f.fieldName === fieldName);
    if (filter) {
      const option = filter.options.find(opt => opt.value === value);
      return option?.displayName || value;
    }
    return value;
  };

  // Convert each search parameter to applied filter format
  Object.entries(searchParams).forEach(([key, value]) => {
    if (!value || key === "currentPage") return;

    const values = ensureArray(value);
    if (values.length === 0) return;

    // Handle special cases
    switch (key) {
      case "PriceFrom":
      case "PriceTo":
        // Handle price range - combine PriceFrom and PriceTo into one filter
        if (searchParams.PriceFrom || searchParams.PriceTo) {
          const priceDisplay = [];
          if (searchParams.PriceFrom) priceDisplay.push(`From $${searchParams.PriceFrom}`);
          if (searchParams.PriceTo) priceDisplay.push(`To $${searchParams.PriceTo}`);
          if (priceDisplay.length > 0) {
            appliedFilters["Price Range"] = [priceDisplay.join(" - ")];
          }
        }
        break;

      case "DepartDateFrom":
      case "DepartDateTo":
        // Handle date range - could be implemented similarly to price
        break;

      case "TourLengthFrom":
      case "TourLengthTo":
        // Handle tour length range - convert back to TourLengthDescription display values
        if (searchParams.TourLengthFrom || searchParams.TourLengthTo) {
          const durationValues = convertRangeToDuration(searchParams.TourLengthFrom, searchParams.TourLengthTo);
          if (durationValues.length > 0) {
            appliedFilters.Duration = durationValues;
          }
        }
        break;

      default: {
        // Handle regular multi-select filters
        const displayValues = values.map(v => findDisplayName(key, v)).filter(Boolean);
        if (displayValues.length > 0) {
          appliedFilters[key] = displayValues;
        }
        break;
      }
    }
  });

  return appliedFilters;
};